/**
 * The marked screenshots of a recent dictation anywhere: a strip of small
 * tiles under the row's text; a tile click copies that one image through
 * Rust. Thumbnails come from `useDictationThumbnails`.
 */
import { Button, Icons } from "@/components/ui";
import { copyDictationAnywhereImage } from "@/desktop/dictation-anywhere/dictation-anywhere-native";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";
import type { DictationThumbnail } from "./use-dictation-thumbnails";

interface VoiceRecentDictationImagesProps {
  historyId: string;
  thumbnails: readonly DictationThumbnail[];
  copied: string | null;
  onCopied: (key: string) => void;
  onError: (message: string | null) => void;
}

export function VoiceRecentDictationImages({
  historyId,
  thumbnails,
  copied,
  onCopied,
  onError,
}: VoiceRecentDictationImagesProps) {
  const t = useT(settingsVoiceMessages);
  const tCommon = useT(commonMessages);
  if (thumbnails.length === 0) return null;
  const total = thumbnails.length;

  async function copy(index: number, key: string) {
    onError(null);
    try {
      await copyDictationAnywhereImage(historyId, index);
      onCopied(key);
    } catch {
      onError(t("copyImageFailed"));
    }
  }

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid={`voice-recent-dictation-images-${historyId}`}
    >
      {thumbnails.map(({ index, url }, position) => {
        const n = position + 1;
        // Shares one "Copied" slot with the row's text Copy buttons.
        const key = `${historyId}:image:${index}`;
        const isCopied = copied === key;
        return (
          <Button
            key={index}
            type="button"
            variant="ghost"
            onClick={() => void copy(index, key)}
            aria-label={
              isCopied
                ? t("imageCopied", { number: n })
                : t("copyImage", { number: n, total })
            }
            data-testid={`voice-recent-dictation-image-${historyId}-${n}`}
            className="relative block h-[45px] w-[72px] shrink-0 overflow-hidden rounded-[8px] border border-[var(--color-glass-stroke-light)] bg-[var(--color-input-fill)] p-0 transition-colors after:absolute after:inset-0 after:rounded-[inherit] after:transition-colors hover:border-[var(--color-subtle)] hover:after:bg-[var(--color-interactive-hover)] focus-visible:ring-2 focus-visible:ring-accent"
          >
            <img
              src={url}
              alt=""
              draggable={false}
              className="block h-full w-full object-cover"
            />
            {isCopied ? (
              <span className="absolute inset-0 z-[1] flex items-center justify-center gap-[3px] bg-[var(--denk-glass-fallback-background)] text-appkit-small font-medium text-primary">
                <Icons.Check className="h-3 w-3" aria-hidden="true" />
                {tCommon("copied")}
              </span>
            ) : null}
          </Button>
        );
      })}
    </div>
  );
}
